<template>
  <div class="image-toolbar" :class="{ 'image-toolbar--light': theme === 'light' }">
    <!-- Left section: Navigation (hidden for single image) -->
    <div class="image-toolbar__section image-toolbar__section--left">
      <template v-if="totalItems > 1">
        <!-- Previous -->
        <v-btn
          :icon="mdiChevronLeft"
          variant="text"
          size="small"
          :disabled="!canPrev"
          @click="emit('prev')"
        />

        <!-- Index display -->
        <span class="image-toolbar__index">
          {{ currentIndex + 1 }} / {{ totalItems }}
        </span>

        <!-- Next -->
        <v-btn
          :icon="mdiChevronRight"
          variant="text"
          size="small"
          :disabled="!canNext"
          @click="emit('next')"
        />
      </template>
    </div>

    <!-- Center section: Zoom -->
    <div v-if="enableZoom" class="image-toolbar__section image-toolbar__section--center">
      <v-btn
        :icon="mdiMagnifyMinusOutline"
        variant="text"
        size="small"
        :disabled="zoom <= 1"
        @click="emit('zoomOut')"
      />

      <span class="image-toolbar__zoom">{{ formatZoomValue(zoom) }}</span>

      <v-btn
        :icon="mdiMagnifyPlusOutline"
        variant="text"
        size="small"
        :disabled="zoom >= maxZoom"
        @click="emit('zoomIn')"
      />

      <v-btn
        v-if="isZoomed"
        :icon="mdiMagnifyRemoveOutline"
        variant="text"
        size="small"
        @click="emit('resetZoom')"
      />
    </div>

    <!-- Right section: Actions -->
    <div class="image-toolbar__section image-toolbar__section--right">
      <!-- Slideshow toggle -->
      <v-btn
        v-if="enableSlideshow && totalItems > 1"
        :icon="slideshowActive ? mdiPause : mdiPlay"
        variant="text"
        size="small"
        :color="slideshowActive ? 'primary' : undefined"
        @click="emit('toggleSlideshow')"
      />

      <v-divider v-if="enableSlideshow && totalItems > 1" vertical class="mx-1" />

      <!-- Download -->
      <v-btn
        v-if="enableDownload"
        :icon="mdiDownload"
        variant="text"
        size="small"
        @click="emit('download')"
      />

      <!-- Fullscreen -->
      <v-btn
        v-if="enableFullscreen"
        :icon="isFullscreen ? mdiFullscreenExit : mdiFullscreen"
        variant="text"
        size="small"
        @click="emit('toggleFullscreen')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiMagnifyPlusOutline,
  mdiMagnifyMinusOutline,
  mdiMagnifyRemoveOutline,
  mdiPlay,
  mdiPause,
  mdiDownload,
  mdiFullscreen,
  mdiFullscreenExit,
} from '@mdi/js'
import { formatZoom } from '../utils/image-utils'

defineProps<{
  currentIndex: number
  totalItems: number
  zoom: number
  maxZoom: number
  isZoomed: boolean
  isFullscreen: boolean
  slideshowActive: boolean
  canPrev: boolean
  canNext: boolean
  enableZoom?: boolean
  enableSlideshow?: boolean
  enableDownload?: boolean
  enableFullscreen?: boolean
  theme?: 'dark' | 'light'
}>()

const emit = defineEmits<{
  prev: []
  next: []
  zoomIn: []
  zoomOut: []
  resetZoom: []
  toggleSlideshow: []
  toggleFullscreen: []
  download: []
}>()

function formatZoomValue(zoom: number): string {
  return formatZoom(zoom)
}
</script>

<style lang="scss" scoped>
// Self-theming — do not rely on the host Vuetify theme (the image player must
// render the same way inside the Developer preview and in a standalone embed).
.image-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #1a1a1a;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
  min-height: 48px;

  &--light {
    background: #f5f5f5;
    border-bottom-color: rgba(0, 0, 0, 0.08);
    color: rgba(0, 0, 0, 0.85);
  }

  // v-btn children inherit the right tone via `currentColor`.
  :deep(.v-btn) {
    color: inherit;
  }

  &__section {
    display: flex;
    align-items: center;
    gap: 4px;

    &--left {
      flex: 1;
      justify-content: flex-start;
    }

    &--center {
      flex: 0 0 auto;
    }

    &--right {
      flex: 1;
      justify-content: flex-end;
    }
  }

  &__index {
    font-size: 13px;
    font-weight: 500;
    min-width: 60px;
    text-align: center;
    color: inherit;
    opacity: 0.9;
  }

  &__zoom {
    font-size: 13px;
    font-weight: 500;
    min-width: 50px;
    text-align: center;
    color: inherit;
    opacity: 0.9;
  }
}
</style>
